import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { Spinner } from '../components/ui';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import IssueCheckInForm from '../components/body-map/IssueCheckInForm';
import IssueEvidencePanel from '../components/body-map/IssueEvidencePanel';
import IssueTrendPanel from '../components/body-map/IssueTrendPanel';
import { getBodyRegionDefinition } from '../data/bodyMapRegions';
import {
  createBodyIssueLink,
  listBodyIssueFeedbackNotes,
  getBodyIssue,
  listBodyIssueCheckIns,
  listBodyIssueEvidenceCandidates,
  listBodyIssueLinks,
  updateBodyIssue,
} from '../services/bodyMapIssueService';
import type { LabResultRecord } from '../types/labs';
import type { BodyIssueEvidenceCandidate, BodyIssueFeedbackNoteRecord, BodyIssueLinkRecord, BodyIssueRecord, BodyIssueCheckInRecord } from '../types/bodyMapIssue';
import { filterVisibleToMember } from '../utils/labResultVisibility';

export default function BodyMapIssuePage() {
  const { currentUser } = useAuth();
  const { issueId } = useParams<{ issueId: string }>();
  const [loading, setLoading] = useState(true);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [issue, setIssue] = useState<BodyIssueRecord | null>(null);
  const [checkIns, setCheckIns] = useState<BodyIssueCheckInRecord[]>([]);
  const [feedbackNotes, setFeedbackNotes] = useState<BodyIssueFeedbackNoteRecord[]>([]);
  const [links, setLinks] = useState<BodyIssueLinkRecord[]>([]);
  const [candidates, setCandidates] = useState<BodyIssueEvidenceCandidate[]>([]);

  const load = useCallback(async () => {
    if (!currentUser || !issueId) return;
    const labResultsSnap = await getDocs(collection(db, 'users', currentUser.uid, 'lab_results'));
    const labResults = filterVisibleToMember(labResultsSnap.docs.map((snapshot) => ({ id: snapshot.id, ...snapshot.data() })) as LabResultRecord[]);
    const [loadedIssue, loadedCheckIns, loadedFeedbackNotes, loadedLinks, loadedCandidates] = await Promise.all([
      getBodyIssue(currentUser.uid, issueId),
      listBodyIssueCheckIns(currentUser.uid, issueId),
      listBodyIssueFeedbackNotes(currentUser.uid, issueId),
      listBodyIssueLinks(currentUser.uid, issueId),
      listBodyIssueEvidenceCandidates(currentUser.uid, labResults),
    ]);
    setIssue(loadedIssue);
    setCheckIns(loadedCheckIns);
    setFeedbackNotes(loadedFeedbackNotes);
    setLinks(loadedLinks);
    setCandidates(loadedCandidates);
  }, [currentUser, issueId]);

  useEffect(() => {
    const run = async () => {
      if (!currentUser || !issueId) return;
      try {
        await load();
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [currentUser, issueId, load]);

  const region = useMemo(
    () => (issue ? getBodyRegionDefinition(issue.regionKey) : null),
    [issue],
  );

  const handleLink = async (candidate: BodyIssueEvidenceCandidate) => {
    if (!currentUser || !issueId) return;
    setLinkingId(candidate.id);
    setNotice(null);
    try {
      await createBodyIssueLink(currentUser.uid, issueId, {
        linkType: candidate.linkType,
        linkedCollection: candidate.linkedCollection,
        linkedDocId: candidate.id,
        label: candidate.label,
        notes: candidate.subtitle || null,
      });
      await load();
      setNotice('Record linked to issue.');
    } finally {
      setLinkingId(null);
    }
  };

  const handleMarkResolved = async () => {
    if (!currentUser || !issue?.id) return;
    await updateBodyIssue(currentUser.uid, issue.id, { status: 'resolved' });
    await load();
    setNotice('Issue marked as resolved.');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <section className="rounded-3xl border p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <h1 className="text-3xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
            Issue not found
          </h1>
          <Link to="/body-map" className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
            Back to body map
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <section className="rounded-3xl border p-8 md:p-10 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
              Body issue
            </p>
            <h1 className="text-3xl md:text-4xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
              {issue.title}
            </h1>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
              {region?.label || issue.regionKey} • {issue.status} • {issue.symptomSummary || 'No summary yet'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/body-map"
              className="inline-flex items-center justify-center px-4 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#EFEEE9', color: 'var(--melio-charcoal)' }}
            >
              Back to body map
            </Link>
            <button
              type="button"
              onClick={handleMarkResolved}
              className="inline-flex items-center justify-center px-4 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              Mark resolved
            </button>
          </div>
        </div>
      </section>

      {notice && (
        <section className="rounded-2xl border px-5 py-4 mt-6 bg-white" style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}>
          {notice}
        </section>
      )}

      <section className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6 mt-6">
        <IssueCheckInForm userId={currentUser!.uid} issue={issue} onSaved={load} />
        <IssueEvidencePanel candidates={candidates} links={links} linkingId={linkingId} onLink={handleLink} />
      </section>

      <section className="mt-6">
        <IssueTrendPanel checkIns={checkIns} feedbackNotes={feedbackNotes} />
      </section>
    </div>
  );
}
